import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTenantAuth } from "../context/auth";
import { useEffect, useMemo, useState } from "react";
import { normalizeLocale, SUPPORTED_LOCALES, type SupportedLocale } from "../lib/locales";
import { cn } from "../lib/cn";

type NavItem = { to: string; key: string; permission?: string };

const nav: NavItem[] = [
  { to: "/app/dashboard", key: "dashboard", permission: "read" },
  { to: "/app/calendar", key: "calendar", permission: "read" },
  { to: "/app/clients", key: "clients", permission: "read" },
  { to: "/app/staff", key: "staff", permission: "read" },
  { to: "/app/services", key: "services", permission: "read" },
  { to: "/app/inventory", key: "inventory", permission: "read" },
  { to: "/app/automation", key: "automation", permission: "configure" },
  { to: "/app/integrations", key: "integrations", permission: "integrations" },
  { to: "/app/branding", key: "branding", permission: "configure" },
  { to: "/app/settings", key: "settings", permission: "configure" },
];

export function AppManagerLayout() {
  const { bootstrap, can, logout, updateTenantLocale } = useTenantAuth();
  const { i18n, t } = useTranslation();
  const [savingLocale, setSavingLocale] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const currentLocale = normalizeLocale(i18n.resolvedLanguage || i18n.language);
  const activeItem = useMemo(
    () => nav.find((item) => location.pathname.startsWith(item.to)),
    [location.pathname]
  );

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="manager-shell">
      <header className="manager-mobile-bar">
        <button
          type="button"
          className="manager-mobile-menu-btn"
          aria-label={t("manager.layout.openNavigation", "Open navigation")}
          onClick={() => setDrawerOpen(true)}
        >
          <span />
          <span />
          <span />
        </button>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400">Salon OS</p>
          <h1 className="text-base font-semibold truncate">{activeItem ? t(`nav.${activeItem.key}`) : t("manager.command")}</h1>
        </div>
      </header>
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className={cn("manager-nav-backdrop", drawerOpen && "open")}
        onClick={() => setDrawerOpen(false)}
      />
      <aside className={cn("manager-nav", drawerOpen && "open")}>
        <div className="rounded-xl border border-white/10 bg-white/5 p-3">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-300">Salon OS</p>
          <h1 className="text-xl font-semibold mt-1">{t("manager.command")}</h1>
          <p className="text-xs text-slate-300 mt-1">{bootstrap?.tenant.name ?? t("manager.layout.tenantFallback")}</p>
          <p className="text-[11px] text-slate-400 mt-2">{t("common.status")}: {bootstrap?.tenant.status ?? t("manager.layout.unknown")}</p>
        </div>

        <div className="mt-4 grid gap-2">
          {nav.filter((item) => !item.permission || can(item.permission)).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? "nav-chip nav-chip-active" : "nav-chip")}
            >
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-3 grid gap-2">
          <label className="text-xs text-slate-300">{t("language")}</label>
          <select
            className="input bg-white/90"
            value={currentLocale}
            disabled={savingLocale}
            onChange={async (e) => {
              const locale = e.target.value as SupportedLocale;
              setSavingLocale(true);
              try {
                await updateTenantLocale(locale);
                await i18n.changeLanguage(locale);
              } finally {
                setSavingLocale(false);
              }
            }}
          >
            {SUPPORTED_LOCALES.map((locale) => (
              <option key={locale} value={locale}>
                {t(`locale.${locale}`)}
              </option>
            ))}
          </select>
          <button className="btn secondary w-full" onClick={logout}>
            {t("logout")}
          </button>
        </div>
      </aside>
      <main className="manager-main" onClick={() => setDrawerOpen(false)}>
        {bootstrap?.tenant.status === "onboarding" ? (
          <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Onboarding mode: finish staff, services, hours, and channel setup to activate this tenant.
          </div>
        ) : null}
        <Outlet />
      </main>
    </div>
  );
}

